import React, { useState, useEffect, useCallback } from 'react';
import {
  KeySquare, Loader2, X, Check, AlertTriangle, Copy, ShieldAlert, Info,
} from 'lucide-react';
import configService from '../../services/configService';

// =============================================================================
// Settings › Single Sign-On.
//
// Stores the tenant's identity-provider configuration in md_config.sso_config.
//
// TWO THINGS THIS SCREEN IS CAREFUL ABOUT, both of which are the difference
// between a configuration form and an incident:
//
//   1. THE CLIENT SECRET IS WRITE-ONLY. It is never sent back by the API, so
//      this screen cannot display it and does not try. It shows whether one is
//      set, and offers to replace it. A secret rendered into an input is a
//      credential in a devtools tab, a screen share and a support screenshot.
//
//   2. ENFORCING SSO CAN LOCK EVERYONE OUT. If the identity provider is
//      unreachable — expired secret, changed tenant, DNS — an enforced tenant
//      has no way back in. So local administrator sign-in is kept as a
//      break-glass, and the server refuses Enforced without it.
//
// The handshake itself is not built. Saying so on screen is better than letting
// somebody switch on a login that cannot complete.
// =============================================================================

const PROVIDERS = [
  { id: 'entra', name: 'Microsoft Entra ID', hint: 'Azure AD — the usual choice for a Microsoft 365 tenant' },
  { id: 'google', name: 'Google Workspace', hint: 'Google as the identity provider' },
  { id: 'okta', name: 'Okta', hint: 'Okta or Auth0 as the identity provider' },
  { id: 'oidc', name: 'Generic OpenID Connect', hint: 'Any provider with a discovery document' },
  { id: 'saml', name: 'SAML 2.0', hint: 'For providers without OIDC — metadata URL instead of a client secret' },
];

const MODES = [
  { id: 'disabled', name: 'Disabled', hint: 'Everyone signs in with a Mantle username and password.' },
  { id: 'optional', name: 'Optional', hint: 'Both are offered. The right choice while you are testing.' },
  { id: 'enforced', name: 'Enforced', hint: 'Single sign-on only, apart from the local administrator escape.' },
];

const EMPTY = {
  provider: 'entra', mode: 'disabled', display_name: '', issuer_url: '', client_id: '',
  email_domains: [], jit_provisioning: false, default_role_urid: null, allow_local_admin: true,
};

export default function SsoConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(EMPTY);
  const [meta, setMeta] = useState({ secret_set: false, redirect_uri: '', handshake_ready: false });
  const [secret, setSecret] = useState('');       // only ever what is being SET now
  const [replacing, setReplacing] = useState(false);
  const [domainText, setDomainText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await configService.getSso();
      const d = res.data || {};
      setCfg({ ...EMPTY, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null)) });
      setMeta({ secret_set: !!d.secret_set, redirect_uri: d.redirect_uri || '', handshake_ready: !!d.handshake_ready });
      setDomainText((d.email_domains || []).join(', '));
      setReplacing(!d.secret_set);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the single sign-on settings.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        ...cfg,
        email_domains: domainText.split(/[,\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean),
      };
      // Only sent when deliberately replacing. Absent means "keep what is
      // stored" — never "clear it".
      if (replacing && secret.trim()) payload.client_secret = secret.trim();
      const res = await configService.saveSso(payload);
      if (res.success) {
        setSaved(true); setSecret(''); await load();
      } else setError(res.message || 'Save failed.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const copyRedirect = () => {
    navigator.clipboard?.writeText(meta.redirect_uri);
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };

  const isSaml = cfg.provider === 'saml';
  const enabled = cfg.mode !== 'disabled';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans" onMouseDown={onClose}>
      <div className="bg-white dark:bg-slate-800 w-full max-w-3xl rounded-xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden"
           onMouseDown={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-2">
            <KeySquare className="w-4 h-4 text-slate-500" />
            <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Single Sign-On</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              {!meta.handshake_ready && (
                <Notice tone="info" icon={Info} title="Configuration only — sign-in is not wired up yet.">
                  These settings are stored and the login page will offer the button, but the redirect and token exchange
                  have not been built. Leave the mode on <strong>Disabled</strong> or <strong>Optional</strong> until they are.
                </Notice>
              )}

              <Field label="Identity provider" hint="Who your people already sign in to.">
                <select value={cfg.provider} onChange={(e) => set('provider', e.target.value)} className={INPUT}>
                  {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <p className="m-0 mt-1 text-[11px] text-slate-400">
                  {PROVIDERS.find((p) => p.id === cfg.provider)?.hint}
                </p>
              </Field>

              <Field label="Button label" hint="What the login page calls it. Blank uses the provider name.">
                <input value={cfg.display_name || ''} onChange={(e) => set('display_name', e.target.value)}
                       placeholder="e.g. Sign in with Acme SSO" className={INPUT} />
              </Field>

              <Field label={isSaml ? 'Metadata URL' : 'Issuer / discovery URL'}
                     hint={isSaml ? 'Your provider\'s SAML metadata document.' : 'The OpenID configuration document, ending in /.well-known/openid-configuration.'}>
                <input value={cfg.issuer_url || ''} onChange={(e) => set('issuer_url', e.target.value)}
                       placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0" className={INPUT} />
              </Field>

              <Field label={isSaml ? 'Entity ID' : 'Client ID'}>
                <input value={cfg.client_id || ''} onChange={(e) => set('client_id', e.target.value)}
                       placeholder={isSaml ? 'urn:mantle:sp' : '00000000-0000-0000-0000-000000000000'} className={INPUT} />
              </Field>

              {!isSaml && (
                <Field label="Client secret"
                       hint="Stored write-only. It is never sent back to this screen, by anyone, including you.">
                  {meta.secret_set && !replacing ? (
                    <div className="flex items-center gap-2">
                      <span className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm text-slate-500 dark:text-slate-400 tabular-nums">
                        •••••••••••••••••••• &nbsp;<span className="text-[11px]">set</span>
                      </span>
                      <button type="button" onClick={() => setReplacing(true)}
                        className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
                        Replace
                      </button>
                    </div>
                  ) : (
                    <>
                      <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                             autoComplete="new-password" placeholder="Paste the secret from your identity provider" className={INPUT} />
                      {meta.secret_set && (
                        <button type="button" onClick={() => { setReplacing(false); setSecret(''); }}
                          className="mt-1 text-[11px] text-slate-500 hover:underline">
                          Keep the existing secret instead
                        </button>
                      )}
                    </>
                  )}
                </Field>
              )}

              <Field label="Redirect URI"
                     hint="Register this with your provider exactly as shown. A mismatch here is the most common reason a first setup fails.">
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-xs text-slate-700 dark:text-slate-300 break-all">
                    {meta.redirect_uri || '—'}
                  </code>
                  <button type="button" onClick={copyRedirect}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </Field>

              <Field label="Federated email domains"
                     hint="Comma separated. Anyone with an address at these domains is sent to the identity provider.">
                <input value={domainText} onChange={(e) => { setDomainText(e.target.value); setSaved(false); }}
                       placeholder="acme.com, acme.co.za" className={INPUT} />
              </Field>

              <label className="flex items-start gap-2 mt-4 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={!!cfg.jit_provisioning}
                       onChange={(e) => set('jit_provisioning', e.target.checked)} />
                <span>
                  <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">Create users on first sign-in</span>
                  <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                    Anyone at a federated domain who authenticates gets a Mantle account automatically. They arrive with no
                    role and therefore no data access until an administrator assigns one.
                  </span>
                </span>
              </label>

              <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Mode</p>
                <div className="grid gap-2">
                  {MODES.map((m) => (
                    <label key={m.id}
                      className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${cfg.mode === m.id
                        ? 'border-slate-800 bg-slate-50 dark:border-slate-400 dark:bg-slate-700/50'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                      <input type="radio" name="sso-mode" className="mt-0.5" checked={cfg.mode === m.id}
                             onChange={() => set('mode', m.id)} />
                      <span>
                        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">{m.name}</span>
                        <span className="block text-[11px] text-slate-500 dark:text-slate-400">{m.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* The break-glass. Deliberately hard to switch off, and the
                  server refuses Enforced without it. */}
              <label className="flex items-start gap-2 mt-4 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={cfg.allow_local_admin !== false}
                       onChange={(e) => set('allow_local_admin', e.target.checked)} />
                <span>
                  <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                    Keep local administrator sign-in
                  </span>
                  <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                    Administrators can still use a Mantle password. This is your way back in if the identity provider is
                    unreachable — an expired secret, a changed tenant, a DNS problem.
                  </span>
                </span>
              </label>

              {cfg.mode === 'enforced' && cfg.allow_local_admin === false && (
                <Notice tone="danger" icon={ShieldAlert} title="This would lock everyone out.">
                  With single sign-on enforced and no local administrator sign-in, nobody can reach Mantle if the identity
                  provider stops responding — including you, and including the screen you would need to undo it.
                </Notice>
              )}

              {enabled && (
                <Notice tone="warn" icon={AlertTriangle} title="Check before you switch this on.">
                  Every person who needs Mantle must exist at your identity provider, and the redirect URI above must be
                  registered there. Test with <strong>Optional</strong> first.
                </Notice>
              )}

              {error && <Notice tone="danger" icon={AlertTriangle} title="Could not save.">{error}</Notice>}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-slate-800/60">
          <span className="text-[11px] text-slate-400">
            {saved ? 'Saved.' : 'Stored in tenant configuration. The secret is write-only.'}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
              Close
            </button>
            <button onClick={save} disabled={saving || loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-600 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const INPUT = 'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 outline-none focus:border-slate-800 dark:focus:border-slate-400';

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</label>
    {hint && <p className="m-0 mb-1.5 text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
    {children}
  </div>
);

const TONES = {
  info: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300',
  warn: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300',
  danger: 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300',
};

const Notice = ({ tone, icon: Icon, title, children }) => (
  <div className={`mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg border ${TONES[tone]}`}>
    <Icon className="w-4 h-4 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="m-0 text-xs font-bold">{title}</p>
      {children && <p className="m-0 mt-1 text-[11px] opacity-90">{children}</p>}
    </div>
  </div>
);
